import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { MagnifyingGlass, EnvelopeSimple, Buildings, Trash, CaretDown, CaretUp, SpinnerGap, Plus, X, Robot, ArrowSquareOut, Copy, Warning } from '@phosphor-icons/react';
import { verifyEmail } from "@/shared/emailValidation";

/* ── NIF Validation (Portuguese checksum) ── */
function validateNIF(value) {
  const nif = String(value);
  const s1 = ['1','2','3','5','6','8'];
  const s2 = ['45','70','71','72','74','75','77','79','90','91','98','99'];
  if (nif.length !== 9) return false;
  if (!s1.includes(nif[0]) && !s2.includes(nif.slice(0,2))) return false;
  let total = 0;
  for (let i = 0; i < 8; i++) total += Number(nif[i]) * (9 - i);
  const mod = total % 11;
  const check = mod < 2 ? 0 : 11 - mod;
  return check === Number(nif[8]);
}

/* ══════════════════════════════════════════════ */
/*  Section 1 — Validar Email                     */
/* ══════════════════════════════════════════════ */
function ValidarEmail() {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/consultant/verifyEmail', { email: email.trim() });
      setResult(data);
    } catch {
      setResult({ success: false, message: 'Erro de comunicação com o servidor.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="section-validar-email">
      <h3 className="text-base font-semibold text-[#111827] mb-4">Validar Email</h3>
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <div className="flex-1 w-full">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Email a validar</label>
          <input
            data-testid="verify-email-input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && verify()}
            placeholder="exemplo@email.com"
            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
          />
        </div>
        <button
          data-testid="verify-email-btn"
          onClick={verify}
          disabled={loading || !email.trim()}
          className="flex items-center gap-2 px-5 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {loading ? <SpinnerGap size={16} className="animate-spin" /> : <EnvelopeSimple size={16} />}
          Verificar
        </button>
      </div>
      {result && (
        <div className="mt-3 space-y-2" data-testid="verify-email-result">
          <div
            className={`px-4 py-2.5 rounded-md border text-sm font-medium ${
              result.exists
                ? 'bg-amber-50 border-amber-300 text-amber-800'
                : result.success
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-yellow-50 border-yellow-300 text-yellow-700'
            }`}
          >
            {result.message}
          </div>
          {result.linked_to && (
            <div className="px-3 py-2 rounded-md border border-[#E5E7EB] bg-[#FAFAF8] text-xs text-[#374151] flex items-start gap-1.5"
              data-testid="verify-email-linked-to">
              <span className="text-[#978D78] uppercase tracking-wider text-[10px] font-bold mt-0.5 shrink-0">Ligado a</span>
              <span className="flex-1 min-w-0">
                <span className="font-medium text-[#0F1829]">{result.linked_to.NomeCompleto || `Cliente #${result.linked_to.ID_Cliente}`}</span>
                {result.linked_to.ID_TipoCliente && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#F5F2EB] text-[#978D78] uppercase tracking-wider">
                    {result.linked_to.ID_TipoCliente}
                  </span>
                )}
                {result.linked_to.Email && (
                  <span className="ml-1.5 text-[#6B7280]">· {result.linked_to.Email}</span>
                )}
                <span className="ml-1.5 text-[#978D78]">(ID #{result.linked_to.ID_Cliente})</span>
              </span>
            </div>
          )}
          {result.consultor && result.consultor.ID_Cliente !== result.linked_to?.ID_Cliente && (
            <div className="px-3 py-2 rounded-md border border-[#E5E7EB] bg-[#FAFAF8] text-xs text-[#374151] flex items-start gap-1.5"
              data-testid="verify-email-consultor">
              <span className="text-[#978D78] uppercase tracking-wider text-[10px] font-bold mt-0.5 shrink-0">Consultor</span>
              <span className="flex-1 min-w-0">
                <span className="font-medium text-[#0F1829]">{result.consultor.NomeCompleto || `Cliente #${result.consultor.ID_Cliente}`}</span>
                {result.consultor.Email && (
                  <span className="ml-1.5 text-[#6B7280]">· {result.consultor.Email}</span>
                )}
                <span className="ml-1.5 text-[#978D78]">(ID #{result.consultor.ID_Cliente})</span>
              </span>
            </div>
          )}
          {result.exists && !result.linked_to && (
            <p className="text-[10px] text-[#978D78]" data-testid="verify-email-no-parent">
              Cliente sem upline registado.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Section 2 — Verificar Empresa Atribuída       */
/* ══════════════════════════════════════════════ */
function VerificarEmpresa({ onCompanyCreated }) {
  const { user } = useAuth();
  const isSolfut = user && Number(user.ID_Cliente) === 1;
  const [nif, setNif] = useState('');
  const [country, setCountry] = useState('PRT');
  const [countries, setCountries] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    api.get('/consultant/countries').then(({ data }) => {
      if (data.success) setCountries(data.data || []);
    }).catch(() => {});
  }, []);

  const verify = async () => {
    if (!nif.trim()) return;
    if (country === 'PRT' && !validateNIF(nif)) {
      setResult({ success: true, assigned: true, message: 'NIF português inválido (falha no checksum).' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/consultant/verifyCompany', { nif: nif.trim(), country });
      setResult(data);
    } catch {
      setResult({ success: false, message: 'Erro de comunicação com o servidor.' });
    } finally {
      setLoading(false);
    }
  };

  const isError = result && (result.assigned || !result.success);
  const isOk = result && result.success && !result.assigned;
  // SOLFUT pode criar quando NIF só aparece por referência (fatura/morada)
  const isReferenceOnly = result && result.success && result.reference_only === true;
  const canCreate = isOk || (isReferenceOnly && isSolfut);

  return (
    <div data-testid="section-verificar-empresa">
      <h3 className="text-base font-semibold text-[#111827] mb-4">Verificar Empresa Atribuída</h3>
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <div className="flex-1 w-full">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">NIF da Empresa</label>
          <input
            data-testid="verify-nif-input"
            type="text"
            value={nif}
            onChange={e => { setNif(e.target.value); setResult(null); }}
            onKeyDown={e => e.key === 'Enter' && verify()}
            placeholder="Introduza o NIF"
            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
          />
        </div>
        <div className="w-full sm:w-40">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">País</label>
          <select
            data-testid="verify-country-select"
            value={country}
            onChange={e => { setCountry(e.target.value); setResult(null); }}
            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none bg-white"
          >
            {countries.length === 0 && <option value="PRT">Portugal</option>}
            {countries.map(c => (
              <option key={c.ID_Pais} value={c.ID_Pais}>{c.Descricao}</option>
            ))}
          </select>
        </div>
        <button
          data-testid="verify-nif-btn"
          onClick={verify}
          disabled={loading || !nif.trim()}
          className="flex items-center gap-2 px-5 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {loading ? <SpinnerGap size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
          Verificar
        </button>
        <button
          data-testid="create-company-btn"
          onClick={() => setShowCreateModal(true)}
          disabled={!canCreate}
          className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          title={
            canCreate
              ? (isReferenceOnly ? 'SOLFUT: criar empresa e alocar a consultor' : 'Criar empresa')
              : (isReferenceOnly ? 'Apenas SOLFUT pode criar nestas condições' : 'Verifique um NIF disponível')
          }
        >
          <Plus size={16} />
          Criar Empresa
        </button>
      </div>
      {result && (
        <div
          data-testid="verify-nif-result"
          className={`mt-3 px-4 py-2.5 rounded-md border text-sm font-medium flex items-start gap-2 ${
            isReferenceOnly
              ? 'bg-amber-50 border-amber-300 text-amber-800'
              : isError ? 'bg-red-50 border-red-300 text-red-700'
                : isOk ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-gray-50 border-gray-300 text-gray-600'
          }`}
        >
          {isReferenceOnly && <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />}
          <span className="flex-1">{result.message}</span>
        </div>
      )}
      {isReferenceOnly && !isSolfut && (
        <p className="mt-2 text-xs text-[#9CA3AF]" data-testid="ref-only-non-solfut-note">
          A ligação a fatura é apenas referência. Só SOLFUT pode criar a empresa e alocá-la a um consultor.
        </p>
      )}

      {showCreateModal && (
        <CriarEmpresaModal
          nif={nif}
          country={country}
          countryName={(countries.find(c => c.ID_Pais === country) || {}).Descricao || country}
          isSolfut={isSolfut}
          referenceOnly={isReferenceOnly}
          suggestedConsultor={result && result.consultor ? result.consultor : null}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            setResult(null);
            setNif('');
            if (onCompanyCreated) onCompanyCreated();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Modal — Criar Empresa                         */
/* ══════════════════════════════════════════════ */
function CriarEmpresaModal({ nif, country, countryName, isSolfut, referenceOnly, suggestedConsultor, onClose, onCreated }) {
  const [form, setForm] = useState({ nome: '', email: '', localidade: '', telemovel: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [consultor, setConsultor] = useState(suggestedConsultor || null);

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  // SOLFUT deve indicar sempre o consultor a quem alocar (referenceOnly ou não)
  const requiresConsultor = isSolfut;

  const handleCreate = async () => {
    if (!form.nome.trim()) { setError('O nome da empresa é obrigatório.'); return; }
    if (form.nome.trim().length < 2) { setError('O nome deve ter pelo menos 2 caracteres.'); return; }
    const ev = await verifyEmail(form.email);
    if (!ev.valid) {
      setError(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
      return;
    }
    if (!form.telemovel.trim() || !/^[+0-9 ]+$/.test(form.telemovel)) { setError('Introduza um telemóvel válido.'); return; }
    if (!form.localidade.trim()) { setError('A localidade é obrigatória.'); return; }
    if (requiresConsultor && (!consultor || !consultor.ID_Cliente)) {
      setError('Indique o consultor a quem alocar a empresa.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        nif, nome: form.nome.trim(), email: form.email.trim(),
        localidade: form.localidade.trim(), telemovel: form.telemovel.trim(), pais: country,
      };
      if (consultor && consultor.ID_Cliente) payload.consultor_id = consultor.ID_Cliente;
      const { data } = await api.post('/consultant/createCompany', payload);
      if (data.success) {
        onCreated();
      } else {
        setError(data.message || 'Erro ao criar empresa.');
      }
    } catch {
      setError('Erro de comunicação com o servidor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="create-company-modal"
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-lg font-semibold text-[#111827]">Criar Empresa</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]" data-testid="close-create-modal">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {referenceOnly && isSolfut && (
            <div className="px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-xs text-amber-900 flex items-start gap-2" data-testid="ref-only-banner">
              <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
              <span>
                Este NIF aparece apenas como referência em faturas/moradas. Está a criar a
                empresa e a alocá-la manualmente a um consultor.
              </span>
            </div>
          )}
          <ModalField label="NIF" value={nif} disabled testid="create-nif" />
          <ModalInput label="Nome" value={form.nome} onChange={v => set('nome', v)} placeholder="Nome da empresa" testid="create-nome" />
          <ModalInput label="Email" value={form.email} onChange={v => set('email', v)} placeholder="email@empresa.pt" type="email" testid="create-email" />
          <ModalInput label="Localidade" value={form.localidade} onChange={v => set('localidade', v)} placeholder="Cidade / Localidade" testid="create-localidade" />
          <ModalInput label="Telemóvel" value={form.telemovel} onChange={v => set('telemovel', v)} placeholder="+351 900 000 000" testid="create-telemovel" />
          <ModalField label="País" value={countryName} disabled testid="create-pais" />
          {isSolfut && (
            <ConsultorPicker
              value={consultor}
              onChange={setConsultor}
              suggested={suggestedConsultor}
            />
          )}
          {error && <p className="text-sm text-red-600 font-medium" data-testid="create-error">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#6B7280] hover:text-[#111827] font-medium transition-colors" data-testid="cancel-create-btn">
            Cancelar
          </button>
          <button
            data-testid="confirm-create-btn"
            onClick={handleCreate}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors"
          >
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} />}
            Criar Empresa
          </button>
        </div>
      </div>
    </div>
  );
}

/* Typeahead de consultores (SOLFUT allocation) */
function ConsultorPicker({ value, onChange, suggested }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (!q || (q.length < 2 && !/^\d+$/.test(q))) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/consultant/searchConsultores', { params: { q, limit: 10 } });
        if (data.success) setResults(data.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (c) => {
    onChange({ ID_Cliente: c.ID_Cliente, NomeCompleto: c.NomeCompleto, Email: c.Email });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="consultor-picker">
      <label className="block text-xs font-medium text-[#6B7280] mb-1">
        Consultor a alocar <span className="text-red-500">*</span>
      </label>
      {value ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border border-[#D1D5DB] rounded-md bg-[#F9FAFB]" data-testid="consultor-picked">
          <div className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-[#111827] truncate">
              {value.NomeCompleto || `Cliente #${value.ID_Cliente}`}
            </span>
            <span className="block text-xs text-[#6B7280] truncate">
              ID #{value.ID_Cliente}{value.Email ? ` · ${value.Email}` : ''}
            </span>
          </div>
          <button
            type="button"
            data-testid="consultor-clear-btn"
            onClick={() => onChange(null)}
            className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]"
            title="Remover"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <input
            data-testid="consultor-search-input"
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Procurar por nome, email ou ID…"
            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
          />
          {open && (query.trim().length >= 2 || /^\d+$/.test(query.trim())) && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E7EB] rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="consultor-search-results">
              {loading && (
                <div className="px-3 py-2 text-xs text-[#6B7280] flex items-center gap-2">
                  <SpinnerGap size={14} className="animate-spin" /> A procurar…
                </div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-[#9CA3AF]">Sem resultados.</div>
              )}
              {!loading && results.map(c => (
                <button
                  key={c.ID_Cliente}
                  type="button"
                  data-testid={`consultor-result-${c.ID_Cliente}`}
                  onClick={() => pick(c)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9FAFB] border-b border-[#F3F4F6] last:border-0"
                >
                  <div className="font-medium text-[#111827]">{c.NomeCompleto || `Cliente #${c.ID_Cliente}`}</div>
                  <div className="text-xs text-[#6B7280]">ID #{c.ID_Cliente}{c.Email ? ` · ${c.Email}` : ''}</div>
                </button>
              ))}
            </div>
          )}
          {suggested && suggested.ID_Cliente && (
            <button
              type="button"
              data-testid="consultor-suggested-btn"
              onClick={() => pick(suggested)}
              className="mt-2 text-xs text-[#0F1829] underline hover:text-[#001f6e]"
            >
              Usar sugerido: {suggested.NomeCompleto} (#{suggested.ID_Cliente})
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ModalField({ label, value, disabled, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#6B7280] mb-1">{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        disabled={disabled}
        readOnly
        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm bg-[#F9FAFB] text-[#6B7280]"
      />
    </div>
  );
}

function ModalInput({ label, value, onChange, placeholder, type = 'text', testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#6B7280] mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
      />
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Section 3 — Minhas Empresas                   */
/* ══════════════════════════════════════════════ */
function MinhasEmpresas() {
  const [companies, setCompanies] = useState([]);
  const [nifFilter, setNifFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/consultant/myCompanies', { nif: nifFilter.trim() });
      if (data.success) setCompanies(data.data || []);
    } catch (e) { console.error("myCompanies load failed", e); }
    finally { setLoading(false); }
  }, [nifFilter]);

  useEffect(() => { loadCompanies(); }, []);

  const deleteCompany = async (id, nome) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar a empresa "${nome}"?`)) return;
    try {
      const { data } = await api.post('/consultant/deleteCompany', { id_empresa: id });
      if (data.success) loadCompanies();
      else alert(data.message);
    } catch {
      alert('Erro ao eliminar empresa.');
    }
  };

  return (
    <div data-testid="section-minhas-empresas">
      <h3 className="text-base font-semibold text-[#111827] mb-4">Minhas Empresas</h3>
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 mb-4">
        <div className="flex-1 w-full">
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Filtrar por NIF</label>
          <input
            data-testid="my-companies-nif-input"
            type="text"
            value={nifFilter}
            onChange={e => setNifFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadCompanies()}
            placeholder="NIF (opcional)"
            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
          />
        </div>
        <button
          data-testid="my-companies-search-btn"
          onClick={loadCompanies}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {loading ? <SpinnerGap size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
          Procurar
        </button>
      </div>

      {/* Table */}
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="my-companies-table">
          <thead>
            <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
              <th className="px-4 py-2.5 text-left font-medium">Nome</th>
              <th className="px-4 py-2.5 text-left font-medium">NIF</th>
              <th className="px-4 py-2.5 text-left font-medium hidden md:table-cell">Localidade</th>
              <th className="px-4 py-2.5 text-left font-medium hidden lg:table-cell">Consultor</th>
              <th className="px-4 py-2.5 text-center font-medium w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {companies.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#9CA3AF]">
                {loading ? 'A carregar...' : 'Sem empresas encontradas.'}
              </td></tr>
            )}
            {companies.map(c => (
              <CompanyRow
                key={c.ID_Empresa}
                company={c}
                expanded={expandedId === c.ID_Empresa}
                onToggle={() => setExpandedId(expandedId === c.ID_Empresa ? null : c.ID_Empresa)}
                onDelete={() => deleteCompany(c.ID_Empresa, c.Nome)}
              />
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <p className="text-xs text-[#9CA3AF] mt-2">{companies.length} empresa(s) encontrada(s)</p>
    </div>
  );
}

function CompanyRow({ company: c, expanded, onToggle, onDelete }) {
  return (
    <>
      <tr
        data-testid={`company-row-${c.ID_Empresa}`}
        className="hover:bg-[#F9FAFB] cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-2.5 font-medium text-[#111827]">
          <div className="flex items-center gap-1.5">
            {expanded ? <CaretUp size={14} className="text-[#9CA3AF]" /> : <CaretDown size={14} className="text-[#9CA3AF]" />}
            {c.Nome}
          </div>
        </td>
        <td className="px-4 py-2.5 text-[#374151]">{c.NIF}</td>
        <td className="px-4 py-2.5 text-[#374151] hidden md:table-cell">{c.Localidade || '-'}</td>
        <td className="px-4 py-2.5 text-[#374151] hidden lg:table-cell">{c.NomeConsultor || '-'}</td>
        <td className="px-4 py-2.5 text-center">
          <button
            data-testid={`delete-company-${c.ID_Empresa}`}
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded hover:bg-red-50 text-[#9CA3AF] hover:text-red-500 transition-colors"
            title="Eliminar empresa"
          >
            <Trash size={16} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="bg-[#F9FAFB] px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <Detail label="Nome" value={c.Nome} />
              <Detail label="NIF" value={c.NIF} />
              <Detail label="Email" value={c.Email} />
              <Detail label="Telemóvel" value={c.Telemovel} />
              <Detail label="Localidade" value={c.Localidade} />
              <Detail label="País" value={c.Pais} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <span className="block text-xs text-[#9CA3AF] mb-0.5">{label}</span>
      <span className="text-[#111827] font-medium">{value || '-'}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Main Page                                      */
/* ══════════════════════════════════════════════ */
/* ══════════════════════════════════════════════ */
/*  YOUR BUSINESS AI — NotebookLM launcher        */
/* ══════════════════════════════════════════════ */
const NOTEBOOK_URL = 'https://notebooklm.google.com/notebook/1e62609c-4465-426e-b2a2-3ffdffef30ed';

function YourBusinessAI() {
  const [copied, setCopied] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const openIt = (e) => {
    e.preventDefault();
    try {
      const w = window.open(NOTEBOOK_URL, '_blank', 'noopener,noreferrer');
      if (!w || w.closed || typeof w.closed === 'undefined') {
        setBlocked(true);
      }
    } catch {
      setBlocked(true);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(NOTEBOOK_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copie o URL:', NOTEBOOK_URL);
    }
  };

  return (
    <div className="relative rounded-xl p-5 shadow-sm overflow-hidden text-white"
      style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F1829 100%)' }}
      data-testid="section-your-business-ai">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 10% 20%, #978D78 0%, transparent 50%), radial-gradient(circle at 90% 80%, #00C853 0%, transparent 40%)' }} />
      <div className="relative flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
        <div className="flex items-center gap-4 min-w-0 w-full">
          <div className="p-3 rounded-lg bg-white/10 backdrop-blur flex-shrink-0">
            <Robot size={28} weight="duotone" className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] font-medium text-white/60 mb-1">Assistente AI</p>
            <h3 className="text-lg font-bold truncate" style={{ fontFamily: "'Fraunces', sans-serif" }}>YOUR BUSINESS AI</h3>
            <p className="text-xs text-white/70 mt-0.5">NotebookLM dedicado ao teu negócio (Google)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a href={NOTEBOOK_URL} target="_blank" rel="noopener noreferrer" onClick={openIt}
            data-testid="open-business-ai-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0F1829] text-sm font-semibold rounded-md hover:bg-white/90 transition-colors whitespace-nowrap">
            Abrir <ArrowSquareOut size={16} weight="bold" />
          </a>
          <button onClick={copyUrl} data-testid="copy-business-ai-btn"
            title="Copiar URL"
            className="p-2 bg-white/10 text-white rounded-md hover:bg-white/20 transition-colors">
            {copied ? '✓' : <Copy size={16} weight="bold" />}
          </button>
        </div>
      </div>
      {blocked && (
        <div className="relative mt-3 bg-amber-50/90 text-amber-900 text-xs px-3 py-2 rounded-md border border-amber-200">
          Se a nova janela não abriu, o browser bloqueou a popup. Usa o botão <strong>Copiar</strong> e cola o URL num novo separador, ou clica-direito em <strong>Abrir → Abrir link em nova tab</strong>.
        </div>
      )}
    </div>
  );
}


export default function ConsultantToolsPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="space-y-0" data-testid="consultant-tools-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Ferramentas Consultor</h1>
        <p className="text-sm text-[#6B7280] mt-1">Ferramentas de apoio ao consultor</p>
      </div>

      <div className="space-y-6">
        {/* YOUR BUSINESS AI — NotebookLM */}
        <YourBusinessAI />

        {/* Validar Email */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
          <ValidarEmail />
        </div>

        {/* Verificar Empresa Atribuída + Criar Empresa */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
          <VerificarEmpresa onCompanyCreated={() => setRefreshKey(k => k + 1)} />
        </div>

        {/* Minhas Empresas */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
          <MinhasEmpresas key={refreshKey} />
        </div>
      </div>
    </div>
  );
}
